import { z } from 'zod';
/**
 * Public policy contract for `/api/v1` (KIR-4431, slice 3).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * This is the first slice with **writes**, and two things about them are worth
 * reading before the schemas.
 *
 * ## Optimistic concurrency
 *
 * Every write accepts an optional `expectedVersion`. Send it and the write is
 * refused with **412** if the record changed since you read it; omit it and the
 * write is last-one-wins. The version is published on the **single-policy
 * read**, not on the list: it is the sum of two independently-versioned rows,
 * so computing it costs two queries per policy and putting it on the list would
 * cost that per row. Read the policy, write with its version — the usual
 * read-then-conditional-write shape.
 *
 * ## 409 and 412 mean different things
 *
 * The internal routes answer 409 for both a stale version and a disabled
 * feature. On this surface they are split, because callers are told to branch
 * on the error *code*:
 *
 * - **409 `Conflict`** — your organization does not have the feature. A person
 *   has to change a setting; retrying will not help.
 * - **412 `PreconditionFailed`** — your `expectedVersion` is stale. Re-read and
 *   retry; this one is a loop your client should run.
 */
/** What a policy does when it matches. */
export const v1PolicyModeSchema = z
    .enum(['off', 'monitor', 'enforce'])
    .describe('What the policy does: `off` — not evaluated; `monitor` — findings are recorded and alerted on; `enforce` — the action is also blocked. `enforce` requires the Enforce-mode feature and a policy that supports it.');
/**
 * One policy as it applies to your organization.
 *
 * **`enabled` is not published.** Internally a policy carries both an `enabled`
 * boolean and a `mode`, which can disagree; `mode` is the one that decides
 * behavior, and `off` already says disabled. Publishing both would invite a
 * caller to read the one that is not authoritative.
 *
 * **The settings blob is not published here.** The internal row can carry a
 * free-form settings record; the settings sub-resource publishes them as typed,
 * described entries instead. Shipping an untyped blob into a frozen contract
 * would freeze whatever happens to be in it.
 */
export const v1PolicySchema = z
    .object({
    key: z
        .string()
        .describe("The policy's stable identifier — for example `mcp-security`. Use it in every policy path."),
    name: z.string().describe('Display name.'),
    description: z
        .string()
        .describe('What the policy detects, in one sentence.'),
    category: z
        .string()
        .describe('How the app groups this policy on the Policies screen, as a stable key — for example `configuration-policies`. Read `categoryName` for the heading the screen actually shows.'),
    categoryName: z
        .string()
        .describe('That group as the Kirin app titles it — e.g. "Allow/Block Lists" for `configuration-policies`, "Detection Policies" for `attack-detection-policies`. Published alongside the key because the two are far enough apart that a customer cannot bridge them.'),
    mode: v1PolicyModeSchema,
    supportsEnforce: z
        .boolean()
        .describe('Whether this policy can enforce at all. Setting `enforce` on a policy that cannot answers 400 — check this first rather than discovering it on the write.'),
    hasSettings: z
        .boolean()
        .describe('Whether this policy has settings you can configure.'),
    settingsCount: z
        .number()
        .int()
        .describe('How many settings it has in total.'),
    customizedSettingsCount: z
        .number()
        .int()
        .describe('How many of those you have changed from the Kirin default. `0` means the policy is running entirely on defaults.'),
})
    .meta({
    id: 'V1Policy',
    title: 'Policy',
    description: 'One Kirin policy as it applies to your organization.',
    examples: [
        {
            key: 'chat-security',
            name: 'Chat & Tool-Call Security',
            description: 'Inspects prompts and responses for secrets, PII and unsafe content.',
            category: 'detection-policies',
            categoryName: 'Detection',
            mode: 'monitor',
            supportsEnforce: true,
            hasSettings: true,
            settingsCount: 6,
            customizedSettingsCount: 2,
        },
    ],
});
/**
 * One policy, read on its own.
 *
 * Identical to the list row plus `version`, which is what a conditional write
 * needs. That is the whole reason to read a policy singly rather than find it
 * in the list.
 */
export const v1PolicyDetailSchema = v1PolicySchema
    .extend({
    version: z
        .number()
        .int()
        .describe("The current version of this policy's configuration. Pass it as `expectedVersion` on a write to have the write refused with 412 if someone changed the policy in between. It moves when either the mode or any setting changes, so it covers both."),
})
    .meta({
    id: 'V1PolicyDetail',
    title: 'Policy detail',
    description: 'One policy, with the version a conditional write needs. Not returned by the list, where computing it per row would cost two queries each.',
    examples: [
        {
            key: 'chat-security',
            name: 'Chat & Tool-Call Security',
            description: 'Inspects prompts and responses for secrets, PII and unsafe content.',
            category: 'detection-policies',
            categoryName: 'Detection',
            mode: 'monitor',
            supportsEnforce: true,
            hasSettings: true,
            settingsCount: 6,
            customizedSettingsCount: 2,
            version: 12,
        },
    ],
});
/** The policy list. Not paginated — an organization has a fixed, small set. */
export const v1PolicyListResponseSchema = z
    .object({
    data: z
        .array(v1PolicySchema)
        .describe('Every policy, in the order the app groups them.'),
})
    .meta({
    id: 'V1PolicyList',
    title: 'Policy list',
    description: 'Every Kirin policy and how it is configured for your organization. Not paginated: the set is fixed and small. Read a single policy to get the version needed for a conditional write.',
    examples: [
        {
            data: [
                {
                    key: 'chat-security',
                    name: 'Chat & Tool-Call Security',
                    description: 'Inspects prompts and responses for secrets, PII and unsafe content.',
                    category: 'detection-policies',
                    categoryName: 'Detection',
                    mode: 'monitor',
                    supportsEnforce: true,
                    hasSettings: true,
                    settingsCount: 6,
                    customizedSettingsCount: 2,
                },
                {
                    key: 'mcp-security',
                    name: 'MCP Security',
                    description: 'Governs which MCP servers may be configured and run.',
                    category: 'configuration-policies',
                    categoryName: 'Allow/Block Lists',
                    mode: 'enforce',
                    supportsEnforce: true,
                    hasSettings: true,
                    settingsCount: 4,
                    customizedSettingsCount: 1,
                },
            ],
        },
    ],
});
// ============================================================================
// Mode write
// ============================================================================
/**
 * Set a policy's mode.
 *
 * **Setting `enforce` while your organization does not have Enforce mode
 * answers 409** rather than quietly recording `monitor`. The internal route
 * downgrades silently, which is right for a UI that also hides the control —
 * but a public caller who asked to enforce and got `200` would believe
 * enforcement was on. That is the kind of wrong a security API must not be.
 */
export const v1PolicyModeUpdateSchema = z
    .object({
    mode: v1PolicyModeSchema.describe('The mode to set.'),
    expectedVersion: z
        .number()
        .int()
        .optional()
        .describe('The `version` you last read for this policy. When present, the write is refused with 412 if the policy changed since. Omit it to write unconditionally.'),
})
    .meta({
    id: 'V1PolicyModeUpdate',
    title: 'Policy mode update',
    description: 'The mode to set on a policy, and optionally the version you expect it to still be at.',
    examples: [{ mode: 'enforce', expectedVersion: 12 }, { mode: 'off' }],
});
// ============================================================================
// Settings
// ============================================================================
/** One configurable setting on a policy. */
export const v1PolicySettingSchema = z
    .object({
    key: z
        .string()
        .describe("The setting's identifier, as used in the settings path."),
    name: z.string().describe('Display name.'),
    description: z.string().describe('What the setting controls.'),
    type: z
        .enum(['boolean', 'string', 'array', 'object'])
        .describe('The shape of `value` and `defaultValue`.'),
    value: z
        .unknown()
        .describe('The value in effect for your organization — your override if you set one, otherwise the Kirin default. Its shape follows `type`.'),
    defaultValue: z
        .unknown()
        .describe('The Kirin default, for comparison. Shape follows `type`.'),
    isCustomized: z
        .boolean()
        .describe('Whether `value` is your override rather than the default. Deleting the setting returns it to `defaultValue` and sets this false.'),
    group: z
        .string()
        .nullable()
        .describe('How the app groups this setting within the policy, or null when it is ungrouped.'),
})
    .meta({
    id: 'V1PolicySetting',
    title: 'Policy setting',
    description: 'One configurable setting on a policy.',
    examples: [
        {
            key: 'severityThreshold',
            name: 'Enforcement severity threshold',
            description: 'The lowest severity that Enforce mode blocks on. Findings below it are recorded only.',
            type: 'string',
            value: 'high',
            defaultValue: 'critical',
            isCustomized: true,
            group: 'Enforcement',
        },
    ],
});
/**
 * A policy's settings.
 *
 * **The validation descriptors are not published.** Each internal setting can
 * carry a `validation` object — pattern, length bounds, nested property shapes —
 * written for the app's form renderer. Publishing it would freeze the app's
 * form metadata into this contract, and it describes how to draw an input
 * rather than what the API accepts. The API's own answer to an invalid value is
 * a 400 naming the problem.
 */
export const v1PolicySettingListResponseSchema = z
    .object({
    data: z
        .array(v1PolicySettingSchema)
        .describe('Every setting on this policy.'),
})
    .meta({
    id: 'V1PolicySettingList',
    title: 'Policy settings',
    description: 'Every configurable setting on a policy, with the value in effect for your organization and the Kirin default beside it. Not paginated.',
    examples: [
        {
            data: [
                {
                    key: 'severityThreshold',
                    name: 'Enforcement severity threshold',
                    description: 'The lowest severity that Enforce mode blocks on. Findings below it are recorded only.',
                    type: 'string',
                    value: 'high',
                    defaultValue: 'critical',
                    isCustomized: true,
                    group: 'Enforcement',
                },
                {
                    key: 'scanChatAttachments',
                    name: 'Scan chat attachments',
                    description: 'Inspect files attached to a chat, not just its text.',
                    type: 'boolean',
                    value: true,
                    defaultValue: true,
                    isCustomized: false,
                    group: 'Detection',
                },
            ],
        },
    ],
});
/**
 * A settings update.
 *
 * `settings` is an object of `key: value` pairs, validated per policy — each
 * policy has its own strict setting schema, so an unknown key, a wrong-typed
 * value, or a value the policy cannot use answers 400 rather than being
 * stored. Only the keys you send change; omitted settings keep their current
 * values.
 */
export const v1PolicySettingsUpdateSchema = z
    .object({
    settings: z
        .record(z.string(), z.unknown())
        .describe('The settings to change, as `key: value`. Only the keys present are changed. Each policy validates its own settings strictly, so an unknown key, a wrong type, or a value the policy cannot use answers 400.'),
    expectedVersion: z
        .number()
        .int()
        .optional()
        .describe('The `version` you last read for this policy. When present, the write is refused with 412 if the policy changed since.'),
})
    .meta({
    id: 'V1PolicySettingsUpdate',
    title: 'Policy settings update',
    description: 'The settings to change on a policy, and optionally the version you expect it to still be at.',
    examples: [
        {
            settings: { severityThreshold: 'high', scanChatAttachments: true },
            expectedVersion: 12,
        },
    ],
});
// ============================================================================
// Exceptions
// ============================================================================
/** What an exception does to the thing it matches. */
export const v1PolicyExceptionActionSchema = z
    .enum(['allow', 'block'])
    .describe('What this exception does to whatever it matches: `allow` exempts it from the policy, `block` blocks it regardless of what the policy would otherwise decide.');
/** Who an exception applies to. */
export const v1PolicyExceptionScopeSchema = z
    .enum(['organization', 'user', 'security-group'])
    .describe('Who this exception applies to. `/api/v1` both creates and returns organization-scoped exceptions only, so this is `organization` on every exception it serves today. The other two are managed in the app; the values stay in this vocabulary because widening what this API surfaces later must not be a breaking change.');
/**
 * One exception to a policy.
 *
 * **`scope` replaces three nullable id columns.** Internally an exception
 * carries `organizationId`, `userId` and `securityGroupId`, exactly one of
 * which is set, and a caller has to know that invariant to read the row.
 * Publishing a `scope` plus the one id that applies says the same thing without
 * requiring the reader to reconstruct the rule.
 */
export const v1PolicyExceptionSchema = z
    .object({
    id: z.string().describe("The exception's identifier."),
    policyKey: z.string().describe('The policy this exception applies to.'),
    action: v1PolicyExceptionActionSchema,
    scope: v1PolicyExceptionScopeSchema,
    scopeId: z
        .string()
        .nullable()
        .describe('The `user` or `security-group` this exception is limited to, or null for an organization-wide one. Null on every exception this API returns today, since it surfaces organization-scoped exceptions only.'),
    rule: z
        .record(z.string(), z.unknown())
        .describe('What the exception matches. The accepted fields depend on the policy — read an existing exception, or the app, to see the shape a given policy uses.'),
    createdAt: z
        .string()
        .datetime()
        .describe('When it was created, ISO 8601 UTC.'),
    updatedAt: z
        .string()
        .datetime()
        .describe('When it last changed, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1PolicyException',
    title: 'Policy exception',
    description: 'One exception to a policy — something the policy should allow, or deny, regardless of what it would otherwise decide.',
    examples: [
        {
            id: '5c8f2a91-6d47-4e03-b1a8-72f9e5c04d63',
            policyKey: 'mcp-security',
            action: 'allow',
            scope: 'organization',
            scopeId: null,
            rule: { serverName: 'github-mcp-server' },
            createdAt: '2026-07-19T10:26:31Z',
            updatedAt: '2026-08-24T09:12:44Z',
        },
    ],
});
/**
 * The exception list query.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1PolicyExceptionListQuerySchema = z.object({
    action: v1PolicyExceptionActionSchema
        .optional()
        .describe('Return only exceptions with this action.'),
});
/** A policy's exceptions. Not paginated. */
export const v1PolicyExceptionListResponseSchema = z
    .object({
    data: z
        .array(v1PolicyExceptionSchema)
        .describe('Every exception on this policy that you can see.'),
})
    .meta({
    id: 'V1PolicyExceptionList',
    title: 'Policy exceptions',
    description: 'The organization-scoped exceptions on a policy. `user`- and `security-group`-scoped exceptions are managed in the app and are not returned here. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '5c8f2a91-6d47-4e03-b1a8-72f9e5c04d63',
                    policyKey: 'mcp-security',
                    action: 'allow',
                    scope: 'organization',
                    scopeId: null,
                    rule: { serverName: 'github-mcp-server' },
                    createdAt: '2026-07-19T10:26:31Z',
                    updatedAt: '2026-08-24T09:12:44Z',
                },
            ],
        },
    ],
});
/** Create an exception. */
export const v1PolicyExceptionCreateSchema = z
    .object({
    action: v1PolicyExceptionActionSchema,
    rule: z
        .record(z.string(), z.unknown())
        .describe('What the exception should match. At least one field must be a non-empty string, and the accepted fields depend on the policy — an unusable rule answers 400.'),
})
    .meta({
    id: 'V1PolicyExceptionCreate',
    title: 'Create a policy exception',
    description: 'A new organization-scoped exception. `user`- and `security-group`-scoped exceptions are created in the app.',
    examples: [{ action: 'allow', rule: { serverName: 'github-mcp-server' } }],
});
/** Replace an exception. Both fields are required — this is a replace, not a patch. */
export const v1PolicyExceptionUpdateSchema = z
    .object({
    action: v1PolicyExceptionActionSchema,
    rule: z
        .record(z.string(), z.unknown())
        .describe('The replacement rule. Same constraints as on create.'),
})
    .meta({
    id: 'V1PolicyExceptionUpdate',
    title: 'Update a policy exception',
    description: 'The new action and rule. This replaces both — it is not a partial update.',
    examples: [{ action: 'block', rule: { serverName: 'unknown-mcp-server' } }],
});
//# sourceMappingURL=policies.js.map